import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";

import { db } from "@/common/platform/firebase";
import {
  Category,
  CategoryDictionary,
  CountryDictionary,
  Industry,
  IndustryDictionary,
  selectHasLoadedClassification,
  selectLoadedOrganizationId,
  setClassification,
  Subcountry,
  SubcountryDictionary,
} from "@/state/slices/classificationSlice";
import { serverErrorNotification } from "@/state/slices/notificationSlice";

const DEPRECATED_CATEGORY_NAMES = new Set(["Media", "Military"]);

const isPlainRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const isString = (value: unknown): value is string => typeof value === "string";

const isSubcountry = (value: unknown): value is Subcountry =>
  isPlainRecord(value) &&
  isString(value.type) &&
  isString(value.name) &&
  isString(value.iso_code) &&
  isString(value.atium_subcountry_id) &&
  isString(value.atium_country_id);

const isIndustry = (value: unknown): value is Industry =>
  isPlainRecord(value) &&
  isString(value.name) &&
  isString(value.atium_id) &&
  isString(value.type);

type RawCategory = Omit<Category, "id">;

const isRawCategory = (value: unknown): value is RawCategory =>
  isPlainRecord(value) &&
  isString(value.atium_id) &&
  isString(value.name) &&
  isString(value.type);

const parseCountries = (raw: unknown): CountryDictionary => {
  if (!isPlainRecord(raw)) return {};
  const result: CountryDictionary = {};
  for (const [key, value] of Object.entries(raw)) {
    if (
      !isPlainRecord(value) ||
      !isString(value.alpha2) ||
      !isString(value.name) ||
      !isString(value.region)
    )
      continue;
    // atium_id may be absent for custom countries (e.g. Somaliland) that have
    // no corresponding entry in the Atium CRM — default to empty string.
    result[key] = {
      alpha2: value.alpha2,
      atium_id: isString(value.atium_id) ? value.atium_id : "",
      name: value.name,
      region: value.region,
    };
  }
  return result;
};

const parseSubcountries = (raw: unknown): SubcountryDictionary => {
  if (!isPlainRecord(raw)) return {};
  const result: SubcountryDictionary = {};
  for (const [key, value] of Object.entries(raw)) {
    if (!Array.isArray(value)) continue;
    result[key] = value.filter(isSubcountry);
  }
  return result;
};

const parseIndustries = (raw: unknown): IndustryDictionary => {
  if (!isPlainRecord(raw)) return {};
  const result: IndustryDictionary = {};
  for (const [key, value] of Object.entries(raw)) {
    if (isIndustry(value)) {
      result[key] = value;
    }
  }
  return result;
};

const parseCategories = (raw: unknown): CategoryDictionary => {
  if (!isPlainRecord(raw)) return {};
  const result: CategoryDictionary = {};
  for (const [key, value] of Object.entries(raw)) {
    const id = Number(key);
    if (!Number.isFinite(id)) continue;
    if (!isRawCategory(value)) continue;
    if (DEPRECATED_CATEGORY_NAMES.has(value.name)) continue;
    result[id] = { ...value, id };
  }
  return result;
};

const errorMessageOf = (reason: unknown): string =>
  reason instanceof Error ? reason.message : String(reason);

const fetchDocument = async (path: string): Promise<unknown> => {
  const snapshot = await getDoc(doc(db, path));
  return snapshot.data();
};

/**
 * Tries the org-specific path first
 * (`organizations/{organizationId}/master_data/{docName}`);
 * falls back to the global path (`master_data/{docName}`) when the
 * org-specific document does not exist.
 */
const fetchDocumentWithOrgFallback = async (
  organizationId: string | undefined,
  docName: string
): Promise<unknown> => {
  if (organizationId !== undefined) {
    const orgPath = `organizations/${organizationId}/master_data/${docName}`;
    try {
      const orgData = await fetchDocument(orgPath);
      if (orgData !== undefined) return orgData;
    } catch {
      // Org-specific path is inaccessible (e.g. permission-denied or not yet
      // provisioned). Fall through to the global master data below.
    }
  }
  return fetchDocument(`master_data/${docName}`);
};

type UseClassificationOptions = {
  /**
   * Read the org's own master data when the route has an `:organizationId` (default). The admin
   * app's `:organizationId` routes edit an org but show the global lists, so it passes false.
   */
  organizationScoped?: boolean;
};

export const useClassification = ({
  organizationScoped = true,
}: UseClassificationOptions = {}): void => {
  const params = useParams<{ organizationId?: string }>();
  const organizationId = organizationScoped ? params.organizationId : undefined;
  const dispatch = useDispatch();
  const loadedForOrganizationId = useSelector(selectLoadedOrganizationId);
  const hasLoaded = useSelector(selectHasLoadedClassification);
  // Re-fetch whenever the active org changes.
  const isLoadedForCurrentOrg =
    hasLoaded && loadedForOrganizationId === (organizationId ?? null);

  useEffect(() => {
    if (isLoadedForCurrentOrg) return;

    let cancelled = false;

    // Each list on its own: one document that fails to load leaves the others usable and is
    // reported by name (Promise.all lost all four and toasted one unnamed error).
    const loadClassification = async () => {
      const [countryDoc, subcountryDoc, industryDoc, categoryDoc] =
        await Promise.allSettled([
          fetchDocumentWithOrgFallback(organizationId, "country_mapping"),
          fetchDocumentWithOrgFallback(organizationId, "subcountry_mapping"),
          fetchDocument("master_data/industry_mapping"),
          fetchDocument("master_data/category_mapping"),
        ]);

      if (cancelled) return;

      const listOf = <List>(
        result: PromiseSettledResult<unknown>,
        key: string,
        parse: (raw: unknown) => List
      ): List | undefined =>
        result.status === "fulfilled"
          ? parse(isPlainRecord(result.value) ? result.value[key] : undefined)
          : undefined;

      dispatch(
        setClassification({
          countries: listOf(countryDoc, "countries", parseCountries),
          subcountries: listOf(
            subcountryDoc,
            "subcountries",
            parseSubcountries
          ),
          industries: listOf(industryDoc, "industries", parseIndustries),
          categories: listOf(categoryDoc, "categories", parseCategories),
          organizationId: organizationId ?? null,
        })
      );

      const lists: [string, PromiseSettledResult<unknown>][] = [
        ["countries", countryDoc],
        ["subcountries", subcountryDoc],
        ["industries", industryDoc],
        ["categories", categoryDoc],
      ];
      for (const [name, result] of lists) {
        if (result.status === "rejected") {
          // A message, not the Error: actions stay serialisable, and admin's notification
          // slice (which shares the action type) reads `message` too.
          dispatch(
            serverErrorNotification({
              message: `Could not load the ${name} list: ${errorMessageOf(
                result.reason
              )}`,
            })
          );
        }
      }
    };

    loadClassification();

    return () => {
      cancelled = true;
    };
  }, [dispatch, isLoadedForCurrentOrg, organizationId]);
};
